import * as Sentry from '@sentry/vue'

/**
 * Report an error to Sentry without ever letting an instrumentation failure
 * surface to the user or break the calling code path.
 *
 * Sentry is wrapped in try/catch on purpose: capturing telemetry must never be
 * able to throw, block bootstrap, or create a user-visible error. Optional
 * `context` is attached as extras so we can see *where* a failure happened
 * (e.g. which sync phase).
 */
export function captureException(
  error: unknown,
  context?: Record<string, unknown>
): void {
  try {
    Sentry.withScope((scope) => {
      if (context) {
        scope.setExtras(context as Record<string, any>)
      }
      Sentry.captureException(error)
    })
  } catch {
    // Swallow — instrumentation must never break the app.
  }
}

type SentryLevel = 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug'

interface CaptureOptions {
  level?: SentryLevel
  tags?: Record<string, string>
  fingerprint?: string[]
  extra?: Record<string, unknown>
}

/**
 * Report a non-exception signal (e.g. a defensive guard tripping) to Sentry as a
 * searchable, alertable message — for conditions that aren't crashes but that we
 * still want visibility into in production (a bare console.* never surfaces
 * there). Wrapped like captureException so instrumentation can never throw or
 * break the calling code path.
 *
 * Two mutually-exclusive call forms (enforced by the overloads):
 *   captureMessage(msg, 'warning', { ...extra })             // severity + extras
 *   captureMessage(msg, { level, tags, fingerprint, extra }) // full scope options
 * The overloads make it impossible to pass a separate `context` alongside the
 * options object, which would otherwise be silently dropped.
 */
export function captureMessage(message: string, level?: SentryLevel, context?: Record<string, unknown>): void
export function captureMessage(message: string, options: CaptureOptions): void
export function captureMessage(
  message: string,
  level: SentryLevel | CaptureOptions = 'warning',
  context?: Record<string, unknown>
): void {
  try {
    // Normalize both forms to one options shape. The string form preserves the
    // original behavior exactly (setLevel + setExtras(context)).
    const options: CaptureOptions =
      typeof level === 'string' ? { level, extra: context } : level
    const { level: severity = 'warning', tags, fingerprint, extra } = options
    // Defensive: the overloads forbid passing `context` alongside an options
    // object, but an untyped/`any` caller still could — fold it in, don't drop it.
    const mergedExtra =
      typeof level !== 'string' && context ? { ...extra, ...context } : extra
    Sentry.withScope((scope) => {
      scope.setLevel(severity)
      if (tags) {
        scope.setTags(tags)
      }
      if (fingerprint) {
        scope.setFingerprint(fingerprint)
      }
      if (mergedExtra) {
        scope.setExtras(mergedExtra as Record<string, any>)
      }
      Sentry.captureMessage(message)
    })
  } catch {
    // Swallow — instrumentation must never break the app.
  }
}

// --- Identity ---------------------------------------------------------------
// Make every Sentry event (errors, messages, replays) searchable by the user's
// callsign and tied to their synced account.
//
// `Sentry.setUser` replaces the whole user object, so we hold the pieces here
// and re-apply a merged identity whenever any one changes. This keeps callsign
// and the synced-account db from clobbering each other regardless of the order
// they become known during boot (sync configures before the profile activates).

let identityCallsign: string | undefined
let identityProfileId: string | undefined
let identitySyncDb: string | undefined

function applyIdentity(): void {
  try {
    Sentry.setUser({
      // Prefer the synced account's db as the stable id so all of an account's
      // callsigns group under one Sentry user; fall back to profile/callsign.
      id: identitySyncDb ?? identityProfileId ?? identityCallsign,
      username: identityCallsign,
    })
    // Dedicated indexed tags so support can filter/alert with `callsign:K9MWD`
    // or `sync_db:userdb-...` directly.
    Sentry.setTag('callsign', identityCallsign)
    Sentry.setTag('sync_db', identitySyncDb)
  } catch {
    // never throw from instrumentation
  }
}

/**
 * Set the active operator's callsign (and profile id) on all subsequent events.
 */
export function setOperatorIdentity(callsign?: string, profileId?: string): void {
  identityCallsign = callsign || undefined
  // Replace (don't preserve) the profile id, so switching operators can't leave
  // stale profile attribution on subsequent events.
  identityProfileId = profileId || undefined
  applyIdentity()
}

/**
 * Tag every subsequent event with the synced account's CouchDB name, derived
 * from the replication URL. Credentials are stripped — only the db name (e.g.
 * `userdb-...`) is ever sent. This identity comes over the network, so it is
 * available even when the local database is corrupt and the callsign cannot be
 * read.
 */
export function setSyncIdentity(dbUrlOrName?: string): void {
  identitySyncDb = dbUrlOrName ? extractDbName(dbUrlOrName) : undefined
  applyIdentity()
}

/**
 * Record every operator callsign on the account so support can see them
 * together (one account often has several, e.g. a primary call and a club/SK).
 */
export function setKnownCallsigns(callsigns: string[]): void {
  try {
    Sentry.setContext('operators', { callsigns })
  } catch {
    // never throw from instrumentation
  }
}

/** Extract just the db name from a replication URL, never leaking credentials. */
function extractDbName(urlOrName: string): string | undefined {
  try {
    // `URL.pathname` excludes the userinfo (user:pass@) entirely.
    const path = new URL(urlOrName).pathname.replace(/^\/+/, '')
    return path || undefined
  } catch {
    // Not a URL — assume it's already a bare db name.
    return urlOrName.replace(/^\/+/, '') || undefined
  }
}
